import { ReloadOutlined, UserOutlined } from '@ant-design/icons'
import { IconChevronDown } from '@tabler/icons-react'
import { Avatar, Button, Dropdown, List } from 'antd'
import type { MenuProps } from 'antd'
import Search from 'antd/es/input/Search'
import classNames from 'classnames'
import React, { useCallback, useMemo, useState } from 'react'

import { Creator } from '@/model/creator'
import { GroupType } from '@/model/group'
import {
  readAllCreators,
  setSelectedCreatorId,
} from '@/redux/creator-actions'
import { selectActivatedAccessibleCreators } from '@/redux/creator-selector'
import {
  createAppSelector,
  useAppDispatch,
  useAppSelector,
} from '@/redux/utils'

import s from './creator-list.module.css'

const selector = createAppSelector([
  selectActivatedAccessibleCreators,
  (state) => state.creator.selectedCreatorId,
  (state) => state.creator.creatorsFetching,
  (state) => state.group.groups,
], (creators, selectedCreatorId, creatorsFetching, groups) => {
  return {
    creators,
    selectedCreatorId,
    creatorsFetching,
    creatorGroups: groups.filter(({ type, ancestorIds }) => type === GroupType.Creator && ancestorIds.length !== 0),
  }
})

export default function CreatorList() {
  const dispatch = useAppDispatch()
  const { selectedCreatorId, creators, creatorsFetching, creatorGroups } = useAppSelector(selector)
  const [search, setSearch] = useState<string>()
  const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>(undefined)

  const onSelectionChange = useCallback((creatorId: number) => {
    dispatch(setSelectedCreatorId(creatorId))
  }, [dispatch])

  const refreshCreators = useCallback(async () => {
    try {
      await dispatch(readAllCreators())
    } catch (ex) {
      console.log('fetching creators error: ', ex)
    }
  }, [dispatch])

  const renderListItem = useCallback(({
    id,
    name,
    avatarUrl,
  }: Creator) => (
    <List.Item
      key={id}
      className={classNames(id === selectedCreatorId && s.SelectedCreator)}
      onClick={() => onSelectionChange(id)}
    >
      <div className={s.CreatorListItem}>
        <div className={s.CreatorAvatar}>
          <Avatar icon={<UserOutlined />} shape="square" size="large" src={avatarUrl} />
        </div>
        <div className={s.CreatorName}>
          <span>{name}</span>
        </div>
      </div>
    </List.Item>
  ), [selectedCreatorId, onSelectionChange])

  const filteredCreators = useMemo(() => creators.filter(({ name, groupId }) => {
    const searchResult = search ? name.toUpperCase().includes(search.toUpperCase()) : true
    const filterGroupResult = selectedGroupId ? groupId === selectedGroupId : true
    return searchResult && filterGroupResult
  }), [creators, search, selectedGroupId])

  const items: MenuProps['items'] = [
    {
      key: 0,
      label: (<a target="_blank" >All Creators</a>),
      onClick: () => setSelectedGroupId(undefined),
    },
    ...creatorGroups.map((group) => ({
      key: group.id,
      label: (<a target="_blank" title={group.name}>{group.name}</a>),
      onClick: () => setSelectedGroupId(group.id),
    })),
  ]

  return (
    <div className={s.CreatorList}>
      <div className={s.Search}>
        <Search
          className={s.SearchInput}
          placeholder="Search"
          onSearch={setSearch}
        />
        <Button onClick={refreshCreators}>
          <ReloadOutlined />
        </Button>
      </div>
      <Dropdown
        overlayClassName={s.Dropdown}
        menu={{ items, selectable: true }}
        trigger={['click']}
      >
        <a className={s.DropdownHeader} onClick={(e) => e.preventDefault()}>
          <span className={s.DropdownTitle}>
            {
              selectedGroupId
                ? creatorGroups.find(({ id }) => id === selectedGroupId)?.name
                : 'All Creators'
            }
          </span>
          <IconChevronDown className={s.DropdownIcon} size={18} />
        </a>
      </Dropdown>
      <List
        rootClassName={s.CreatorListContent}
        dataSource={filteredCreators}
        split={false}
        loading={creatorsFetching}
        renderItem={renderListItem}
      />
    </div>
  )
}
